import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ChartBar, CaretLeft, CaretRight, Warning, UsersThree, Sparkle, Kanban,
  Fire, Trophy, CheckCircle,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { DashboardZone, ActionTile, AllClear, MetricsDisclosure } from '@/components/enterprise/DashboardZone';
import { engApi } from './engApi';

const DOW = ['月', '火', '水', '木', '金', '土', '日'];

const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString('sv-SE');
};
const mondayOf = (d = new Date()) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x.toLocaleDateString('sv-SE');
};

const RANK_STYLE = [
  'bg-amber-100 text-amber-700 border-amber-300',
  'bg-zinc-200 text-zinc-600 border-zinc-300',
  'bg-orange-100 text-orange-700 border-orange-200',
];

function StreakBoard({ items }) {
  return (
    <div className="rounded-lg border border-zinc-200 p-3" data-testid="dash-streak-board">
      <h3 className="text-[12px] font-semibold text-zinc-700 flex items-center gap-1.5">
        <Fire size={13} weight="duotone" className="text-zinc-400" /> チームストリークボード
      </h3>
      <p className="text-[11px] text-zinc-400 mt-0.5">平日ベースの連続提出記録</p>
      {items.length === 0 ? (
        <p className="text-[12px] text-zinc-400 mt-3">まだ提出記録がありません</p>
      ) : (
        <ul className="mt-2 divide-y divide-zinc-200">
          {items.map((m, i) => (
            <li key={m.id} className="py-2 flex items-center gap-2.5" data-testid={`streak-board-row-${m.id}`}>
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full border text-[11px] font-bold shrink-0 ${RANK_STYLE[i] || 'bg-white text-zinc-400 border-zinc-200'}`}>
                {i + 1}
              </span>
              <span className="text-[13px] font-semibold text-zinc-800 truncate">{m.name}</span>
              {m.today_submitted && <CheckCircle size={13} weight="fill" className="text-emerald-500 shrink-0" title="本日提出済み" />}
              <span className="ml-auto flex items-center gap-3 shrink-0">
                <span className={`inline-flex items-center gap-0.5 text-[13px] font-bold ${m.current_streak > 0 ? 'text-orange-600' : 'text-zinc-300'}`}>
                  <Fire size={13} weight="duotone" /> {m.current_streak}
                  <span className="text-[10px] font-semibold text-zinc-400">日</span>
                </span>
                <span className="inline-flex items-center gap-0.5 text-[11px] text-zinc-400" title="自己ベスト">
                  <Trophy size={11} weight="duotone" className="text-amber-500" /> {m.best_streak}
                </span>
                <span className="text-[11px] text-zinc-400 hidden sm:inline">今週 {m.week_count}件</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function EngReportDashboard() {
  const [weekStart, setWeekStart] = useState(mondayOf());
  const [data, setData] = useState(null);
  const [streaks, setStreaks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { engApi.teamStreaks().then(setStreaks).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    engApi.reportsDashboard({ date_from: weekStart, date_to: addDays(weekStart, 6) })
      .then(setData)
      .catch((e) => toast.error(e.response?.data?.detail || 'ダッシュボードの読み込みに失敗しました'))
      .finally(() => setLoading(false));
  }, [weekStart]);

  const maxTrend = Math.max(1, ...((data?.trend || []).map((t) => t.count)));

  return (
    <div className="space-y-4" data-testid="eng-report-dashboard-page">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" asChild className="h-8 -ml-2 text-zinc-500">
          <Link to="/engineering/reports"><ArrowLeft size={14} className="mr-1" /> 日報一覧</Link>
        </Button>
        <h1 className="text-lg sm:text-xl font-bold text-zinc-900 flex items-center gap-2">
          <ChartBar size={22} weight="duotone" className="text-teal-600" /> 日報ダッシュボード
        </h1>
        <div className="ml-auto flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="dash-prev-week"><CaretLeft size={14} /></Button>
          <span className="text-[12px] font-semibold text-zinc-600 min-w-[150px] text-center" data-testid="dash-week-label">{weekStart} 〜 {addDays(weekStart, 6)}</span>
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="dash-next-week"><CaretRight size={14} /></Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="bg-white rounded-lg border border-zinc-200 px-5 py-14 text-center">
          <div className="w-7 h-7 mx-auto rounded-full border-4 border-zinc-200 border-t-teal-600 animate-spin" />
        </div>
      ) : (
        <>
          <DashboardZone title="対応が必要" hint="今週の未提出とブロッカーだけを表示します" testid="eng-report-action-zone">
            {data.missing.length === 0 && data.blocker_projects.length === 0 ? (
              <AllClear message="未提出者もブロッカーもありません" testid="dash-allclear" />
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <ActionTile icon={UsersThree} label="未提出者" hint="今週まだ提出なし" value={data.missing.length} urgent
                  onClick={() => { const el = document.querySelector('[data-testid="dash-missing-card"]'); el?.scrollIntoView({ behavior: 'smooth' }); }}
                  testid="dash-missing-count" />
                <ActionTile icon={Warning} label="ブロッカー" hint="ブロッカーのあるプロジェクト" value={data.blocker_projects.length} urgent
                  onClick={() => { const el = document.querySelector('[data-testid="dash-blockers-card"]'); el?.scrollIntoView({ behavior: 'smooth' }); }}
                  testid="dash-blocker-count" />
              </div>
            )}
          </DashboardZone>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-zinc-200 p-4" data-testid="dash-missing-card">
              <h3 className="text-[13px] font-semibold text-zinc-900 flex items-center gap-1.5"><UsersThree size={14} weight="duotone" className="text-zinc-400" /> 未提出のメンバー</h3>
              {data.missing.length === 0 ? (
                <p className="text-[12px] text-emerald-600 mt-3 font-medium">全員が今週の日報を提出済みです</p>
              ) : (
                <ul className="mt-2 divide-y divide-zinc-200">
                  {data.missing.map((m) => (
                    <li key={m.id} className="py-1.5 flex items-center gap-2 text-[13px]" data-testid={`dash-missing-${m.id}`}>
                      <span className="text-zinc-700">{m.name}</span>
                      <span className="ml-auto text-[11px] text-zinc-400">{m.last_report_date ? `最終提出: ${m.last_report_date}` : '提出履歴なし'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white rounded-lg border border-zinc-200 p-4" data-testid="dash-blockers-card">
              <h3 className="text-[13px] font-semibold text-zinc-900 flex items-center gap-1.5"><Warning size={14} weight="duotone" className="text-rose-500" /> ブロッカーのあるプロジェクト</h3>
              {data.blocker_projects.length === 0 ? (
                <p className="text-[12px] text-zinc-400 mt-3">ブロッカーの報告はありません</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {data.blocker_projects.map((p) => (
                    <li key={p.project_id || p.project_name} className="rounded-lg border border-rose-100 bg-rose-50/50 px-3 py-2">
                      <p className="text-[13px] font-medium text-zinc-900">{p.project_name}</p>
                      {p.blockers.slice(0, 3).map((b, i) => (
                        <p key={i} className="text-[11px] text-zinc-600 mt-0.5">
                          <span className="text-zinc-400">{b.report_date} {b.engineer_name}:</span> {b.text}
                        </p>
                      ))}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-zinc-200 p-4 flex flex-wrap items-center gap-3" data-testid="dash-digest-card">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-zinc-100 text-zinc-500 shrink-0">
              <Sparkle size={17} weight="duotone" />
            </span>
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-zinc-900">AIダイジェスト</p>
              <p className="text-[11px] text-zinc-500">
                {data.latest_digest
                  ? `最終生成: ${data.latest_digest.week_start} 〜 ${data.latest_digest.week_end}（日報 ${data.latest_digest.report_count}件）`
                  : 'まだ生成されていません'}
                {' ・ '}
                {data.digest_settings.digest_enabled
                  ? `自動生成: ${DOW[data.digest_settings.digest_day]}曜 ${data.digest_settings.digest_hour}:00`
                  : '自動生成: 無効'}
              </p>
            </div>
            <Button size="sm" variant="outline" asChild className="h-9 ml-auto" data-testid="dash-digest-link">
              <Link to="/engineering/reports/digests">ダイジェストを見る</Link>
            </Button>
          </div>

          <MetricsDisclosure title="参考指標（今週のアクティビティ）" testid="eng-report-metrics">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
              <div className="rounded-lg border border-zinc-200 p-3" data-testid="dash-submitted-count">
                <p className="text-[11px] text-zinc-500">今週の提出数</p>
                <p className="text-lg font-bold text-zinc-900 tabular-nums mt-0.5">{data.submitted_count}</p>
                <p className="text-[11px] text-zinc-400">{data.engineer_count} 名のエンジニア</p>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3" data-testid="dash-lessons-count">
                <p className="text-[11px] text-zinc-500">教訓・学び</p>
                <p className="text-lg font-bold text-zinc-900 tabular-nums mt-0.5">{data.lessons_count}</p>
                <p className="text-[11px] text-zinc-400">今週記録された学び</p>
              </div>
              <div className="rounded-lg border border-zinc-200 p-3" data-testid="dash-trend-card">
                <p className="text-[11px] text-zinc-500">週間アクティビティ</p>
                <div className="mt-2 flex items-end gap-1.5 h-16">
                  {data.trend.map((tr, i) => (
                    <div key={tr.date} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full rounded-t bg-teal-600/70" style={{ height: `${Math.max(3, (tr.count / maxTrend) * 48)}px`, opacity: tr.count ? 1 : 0.15 }} />
                      <span className="text-[9px] text-zinc-400">{DOW[i]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
              <div className="rounded-lg border border-zinc-200 p-3" data-testid="dash-active-projects-card">
                <h3 className="text-[12px] font-semibold text-zinc-700 flex items-center gap-1.5"><Kanban size={13} weight="duotone" className="text-zinc-400" /> 活発なプロジェクト</h3>
                {data.active_projects.length === 0 ? (
                  <p className="text-[12px] text-zinc-400 mt-2">今週の提出はまだありません</p>
                ) : (
                  <ul className="mt-1.5 divide-y divide-zinc-200">
                    {data.active_projects.map((p) => (
                      <li key={p.project_id || p.project_name} className="py-1.5 flex items-center gap-2 text-[13px]">
                        <span className="truncate text-zinc-700">{p.project_name}</span>
                        <span className="ml-auto text-[11px] font-medium text-zinc-600 tabular-nums">{p.count}件</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <StreakBoard items={streaks} />
            </div>
          </MetricsDisclosure>
        </>
      )}
    </div>
  );
}
